import { NO } from "uicore-ts"
import { LanguageService } from "../../LanguageService"
import { LabeledSearchableDropdown } from "../LabeledSearchableDropdown"
import { SearchableDropdown } from "../SearchableDropdown"
import { RHColor } from "./RHColor"
import { RHForms } from "./Utils_slop/RHDesignTokens"


// MARK: - Internal Styled Dropdown

class RHSearchableDropdown<T> extends SearchableDropdown<T> {
    
    constructor(elementID?: string) {
        
        super(elementID)
        
        // Fixed-height button — children reporting intrinsic size changes upward
        // causes a layout cycle because updateTitleWithSelection is called every
        // layoutSubviews pass. Disable the propagation; height is always set by
        // the parent layout, never derived from children.
        this.usesVirtualLayoutingForIntrinsicSizing = NO
        
        // Trigger button: Bootstrap .form-control appearance
        this.configureWithObject({
            //@ts-ignore
            style: {
                boxSizing: "border-box",
                backgroundColor: RHColor.surface.stringValue,
                padding: RHForms.dropdownControlPadding,
                fontSize: RHForms.dropdownControlFontSize,
                lineHeight: RHForms.dropdownControlLineHeight,
                color: RHColor.textPrimary.stringValue,
                minHeight: `${RHForms.dropdownControlHeight}px`,
                display: "flex",
                alignItems: "center",
                transition: RHForms.dropdownControlTransition,
                borderRadius: RHForms.dropdownControlBorderRadius,
                appearance: "none"
            }
        })
        this.titleLabel?.configureWithObject({
            style: {
                fontSize: RHForms.dropdownControlFontSize,
                lineHeight: RHForms.dropdownControlLineHeight
            }
        })
        this.overflowLabel?.configureWithObject({
            style: {
                fontSize: RHForms.dropdownControlFontSize,
                lineHeight: RHForms.dropdownControlLineHeight
            }
        })
        
        // Dropdown menu container: Bootstrap .dropdown-menu appearance
        this._containerView.configureWithObject({
            style: {
                backgroundColor: RHColor.surface.stringValue,
                borderRadius: RHForms.dropdownMenuBorderRadius,
                border: `${RHForms.dropdownMenuBorderWidth} solid ${RHColor.border.stringValue}`,
                boxShadow: RHForms.dropdownMenuBoxShadow,
                padding: RHForms.dropdownMenuPadding,
                marginTop: RHForms.dropdownMenuTopMargin
            }
        })
        
        // Search input inside the menu
        this._searchTextField.configureWithObject({
            style: {
                border: `${RHForms.dropdownSearchFieldBorderWidth} solid ${RHColor.border.stringValue}`,
                borderRadius: RHForms.dropdownSearchFieldBorderRadius,
                padding: "0",
                margin: RHForms.dropdownSearchFieldMargin,
                height: `${RHForms.dropdownSearchFieldHeight}px`,
                display: "flex",
                alignItems: "stretch",
                backgroundColor: RHColor.surface.stringValue,
                overflow: "hidden",
                boxSizing: "border-box"
            }
        })
        this._searchTextField.placeholderText = LanguageService.stringForCurrentLanguage({
            en: "Search",
            est: "Otsi"
        })

        this._searchTextField.searchButton.configureWithObject({
            style: {
                width: `${RHForms.dropdownSearchButtonWidth}px`,
                height: "100%",
                border: "none",
                borderRight: `${RHForms.dropdownSearchFieldBorderWidth} solid ${RHColor.border.stringValue}`,
                backgroundColor: RHForms.dropdownSearchButtonBackgroundColor.stringValue,
                color: RHColor.textSecondary.stringValue,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "0",
                cursor: "default",
                fontSize: RHForms.dropdownSearchButtonIconFontSize,
                lineHeight: RHForms.dropdownSearchButtonIconLineHeight
            }
        })

        this._searchTextField.textField.configureWithObject({
            style: {
                border: "none",
                outline: "none",
                flex: "1",
                height: "100%",
                padding: RHForms.dropdownSearchInputPadding,
                fontSize: RHForms.dropdownSearchInputFontSize,
                lineHeight: RHForms.dropdownSearchInputLineHeight,
                backgroundColor: "transparent",
                color: RHColor.textPrimary.stringValue
            }
        })
        
    }
    
    
    override updateContentForNormalState() {
        super.updateContentForNormalState()
        this.style.border = `1px solid ${RHColor.border.stringValue}`
        this.style.boxShadow = "none"
        this.titleLabel.textColor = RHColor.textPrimary
    }
    
    
    override updateContentForHighlightedState() {
        super.updateContentForHighlightedState()
        const focusColor = RHColor.borderFocus
        this.style.borderColor = focusColor.stringValue
        this.style.boxShadow = `0 0 0 0.25rem ${focusColor.colorWithAlpha(0.25).stringValue}`
        this.titleLabel.textColor = RHColor.textPrimary
    }
    
    
}


// MARK: - RHLabeledSearchableDropdown

export class RHLabeledSearchableDropdown<T> extends LabeledSearchableDropdown<T> {
    
    override dropdown!: RHSearchableDropdown<T>
    
    constructor(elementId?: string) {
        
        super(elementId)
        
        this.dropdown.removeFromSuperview()
        this.dropdown = new RHSearchableDropdown<T>(this.elementID + "Dropdown")
            .addedAsSubviewToView(this)
        
        this.dropdown.expandedContainerViewHeight = RHForms.dropdownExpandedContainerHeight
        
        this.label.configureWithObject({
            textColor: RHForms.dropdownLabelTextColor,
            style: {
                fontWeight: RHForms.dropdownLabelFontWeight,
                fontSize: RHForms.dropdownLabelFontSize,
                lineHeight: RHForms.dropdownLabelLineHeight,
                letterSpacing: "0",
                textTransform: "none"
            }
        })
        
    }
    
    
    override layoutSubviews() {
        
        super.layoutSubviews()
        
        this.contentBoundsWithInset(0)
            .rectangleWithHeight(RHForms.dropdownLabelHeight)
            .assignedAsFrameOfView(this.label)
            .rectangleForNextRow(
                RHForms.dropdownLabelControlGap,
                RHForms.dropdownControlHeight
            )
            .assignedAsFrameOfView(this.dropdown)
        
    }
    
}
